import React, {
  useState,
  useRef,
  useEffect,
  KeyboardEvent,
  useImperativeHandle,
  forwardRef,
} from "react";
import { Root, Trigger, Content, Portal } from "@radix-ui/react-popover";
import { Check, ChevronsUpDown } from "lucide-react";
import { cn } from "utils/cn";
import { useController, useFormContext } from "react-hook-form";

// Types
import { SearchableDropdownProps } from "./SearchableDropdown.types";
import { Button, type ButtonAccentColor } from "../buttons";
import { InputV3 } from "../input/InputV3";
import { capitalize } from "utils";

type ActiveColorClasses = {
  chip: string;
  badge: string;
  hover: string;
  selected: string;
  check: string;
  ring: string;
};

// Hue text stays on at most a 15% tint so it reads on every preset.
const EMERALD_ACTIVE: ActiveColorClasses = {
  chip: "bg-hue-emerald/15 text-hue-emerald border border-hue-emerald/30",
  badge: "bg-card text-hue-emerald",
  hover: "bg-hue-emerald/10",
  selected: "bg-hue-emerald/15 text-hue-emerald",
  check: "text-hue-emerald",
  ring: "ring-hue-emerald",
};

const VIOLET_ACTIVE: ActiveColorClasses = {
  chip: "bg-hue-violet/15 text-hue-violet border border-hue-violet/30",
  badge: "bg-card text-hue-violet",
  hover: "bg-hue-violet/10",
  selected: "bg-hue-violet/15 text-hue-violet",
  check: "text-hue-violet",
  ring: "ring-hue-violet",
};

const activeColorMap: Record<ButtonAccentColor, ActiveColorClasses> = {
  indigo: {
    chip: "bg-hue-indigo/15 text-hue-indigo border border-hue-indigo/30",
    badge: "bg-card text-hue-indigo",
    hover: "bg-hue-indigo/10",
    selected: "bg-hue-indigo/15 text-hue-indigo",
    check: "text-hue-indigo",
    ring: "ring-hue-indigo",
  },
  violet: VIOLET_ACTIVE,
  emerald: EMERALD_ACTIVE,
  cyan: {
    chip: "bg-hue-cyan/15 text-hue-cyan border border-hue-cyan/30",
    badge: "bg-card text-hue-cyan",
    hover: "bg-hue-cyan/10",
    selected: "bg-hue-cyan/15 text-hue-cyan",
    check: "text-hue-cyan",
    ring: "ring-hue-cyan",
  },
  teal: {
    chip: "bg-hue-teal/15 text-hue-teal border border-hue-teal/30",
    badge: "bg-card text-hue-teal",
    hover: "bg-hue-teal/10",
    selected: "bg-hue-teal/15 text-hue-teal",
    check: "text-hue-teal",
    ring: "ring-hue-teal",
  },
  blue: {
    chip: "bg-hue-blue/15 text-hue-blue border border-hue-blue/30",
    badge: "bg-card text-hue-blue",
    hover: "bg-hue-blue/10",
    selected: "bg-hue-blue/15 text-hue-blue",
    check: "text-hue-blue",
    ring: "ring-hue-blue",
  },
  brand: {
    chip: "bg-primary/20 text-primary border border-primary/30",
    badge: "bg-primary/30 text-secondary",
    hover: "bg-primary/10",
    selected: "bg-primary/20 text-primary",
    check: "text-primary",
    ring: "ring-primary",
  },
  gray: {
    chip: "border border-border bg-muted text-muted-foreground",
    badge: "bg-muted text-muted-foreground",
    hover: "bg-muted/70",
    selected: "bg-muted text-muted-foreground",
    check: "text-muted-foreground",
    ring: "ring-ring",
  },
  red: {
    chip: "bg-danger/20 text-danger border border-danger/30",
    badge: "bg-danger/30 text-danger",
    hover: "bg-danger/10",
    selected: "bg-danger/20 text-danger",
    check: "text-danger",
    ring: "ring-danger",
  },
  green: EMERALD_ACTIVE,
  yellow: {
    chip: "bg-warning/20 text-warning border border-warning/30",
    badge: "bg-warning/30 text-warning",
    hover: "bg-warning/10",
    selected: "bg-warning/20 text-warning",
    check: "text-warning",
    ring: "ring-warning",
  },
  purple: VIOLET_ACTIVE,
  claude: {
    chip: "bg-connector/20 text-connector border border-connector/30",
    badge: "bg-connector/30 text-connector",
    hover: "bg-connector/10",
    selected: "bg-connector/20 text-connector",
    check: "text-connector",
    ring: "ring-connector",
  },
};

export const SearchableDropdown = forwardRef<{ clear: () => void }, SearchableDropdownProps>(
  (
    {
      name,
      label,
      options,
      placeholder = "Select an option",
      searchPlaceholder = "Search...",
      disabled = false,
      onChange: externalOnChange,
      value: externalValue,
      noFormContext = false,
      showActionButton = false,
      actionButtonContent = "",
      onActionButtonClick = () => {},
      actionButtonClassName = "",
      dropdownRef,
      triggerClassName = "",
      contentClassName = "",
      variant = "default",
      color = "violet",
    },
    ref
  ) => {
    // State for managing the component
    const [open, setOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const [highlightedIndex, setHighlightedIndex] = useState(-1);
    const [activeDescendant, setActiveDescendant] = useState("");

    // Navigation mode state
    const [navigationMode, setNavigationMode] = useState<"mouse" | "keyboard">("mouse");
    const [isWrappingToTop, setIsWrappingToTop] = useState(false);

    // Track when user last used keyboard navigation for timeout purposes
    const lastKeyboardNavTime = useRef<number>(0);

    // Time in ms to block mouse interactions after keyboard navigation
    const keyboardLockoutPeriod = 500;

    // Refs
    const inputRef = useRef<HTMLInputElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const listboxRef = useRef<HTMLDivElement>(null);
    const optionRefs = useRef<Record<string, HTMLButtonElement | null>>({});

    // Form context integration
    const formContext = useFormContext();
    const controller = noFormContext
      ? null
      : useController({
          name,
          control: formContext?.control,
        });

    // Get value and onChange handler based on whether we're using form context
    const value = noFormContext ? externalValue : controller?.field.value;
    const onChange = noFormContext ? externalOnChange : controller?.field.onChange;
    const error = noFormContext ? undefined : controller?.fieldState.error;

    // Expose clear function via ref
    useImperativeHandle(
      ref || dropdownRef,
      () => ({
        clear: () => {
          if (onChange) {
            onChange("");
          }
          setSearchTerm("");
          setHighlightedIndex(-1);
          setActiveDescendant("");
        },
      }),
      [onChange]
    );

    // Filter options based on search term
    const filteredOptions = searchTerm
      ? options.filter(
          (option) =>
            option.label.toLowerCase().includes(searchTerm.toLowerCase()) ||
            option.value.toLowerCase().includes(searchTerm.toLowerCase())
        )
      : options;

    // Get display label for the current value
    const getSelectedLabel = () => {
      if (!value) return placeholder;
      const option = options.find((option) => option.value === value);
      return option ? capitalize(option.label) : placeholder;
    };

    // Reset state when dropdown opens
    useEffect(() => {
      if (open) {
        // Focus the input when opened
        setTimeout(() => {
          if (inputRef.current) {
            inputRef.current.focus();
          }
        }, 10);

        // Highlight the selected option if it exists in the filtered list
        if (value) {
          const index = filteredOptions.findIndex((option) => option.value === value);
          if (index >= 0) {
            setHighlightedIndex(index);
            setActiveDescendant(`option-${filteredOptions[index].value}`);
          } else {
            setHighlightedIndex(0);
            setActiveDescendant(
              filteredOptions.length > 0 ? `option-${filteredOptions[0].value}` : ""
            );
          }
        } else {
          // Otherwise highlight the first option if available
          setHighlightedIndex(filteredOptions.length > 0 ? 0 : -1);
          setActiveDescendant(
            filteredOptions.length > 0 ? `option-${filteredOptions[0].value}` : ""
          );
        }

        // Reset navigation mode when opening
        setNavigationMode("mouse");
        setIsWrappingToTop(false);
        lastKeyboardNavTime.current = 0;
      } else {
        // Reset everything when closing
        setSearchTerm("");
        setHighlightedIndex(-1);
        setActiveDescendant("");
        setNavigationMode("mouse");
        setIsWrappingToTop(false);
        lastKeyboardNavTime.current = 0;
      }
    }, [open, filteredOptions.length, value]);

    // Update highlight when filtered options change (due to search)
    useEffect(() => {
      if (open) {
        if (filteredOptions.length === 0) {
          setHighlightedIndex(-1);
          setActiveDescendant("");
        } else if (highlightedIndex >= filteredOptions.length || highlightedIndex === -1) {
          // Reset to first item if the current highlight is out of bounds
          setHighlightedIndex(0);
          setActiveDescendant(`option-${filteredOptions[0].value}`);
        }
      }
    }, [filteredOptions, open, highlightedIndex]);

    // Handle special scroll when wrapping from bottom to top
    useEffect(() => {
      if (isWrappingToTop && listboxRef.current) {
        // Reset scroll position to top when wrapping around
        listboxRef.current.scrollTop = 0;
        setIsWrappingToTop(false);
      }
    }, [isWrappingToTop]);

    // Scroll highlighted option into view - but only when keyboard navigation is being used
    useEffect(() => {
      // Only auto-scroll when navigating via keyboard and not wrapping to top
      if (
        navigationMode === "keyboard" &&
        !isWrappingToTop &&
        highlightedIndex >= 0 &&
        highlightedIndex < filteredOptions.length &&
        listboxRef.current &&
        optionRefs.current[filteredOptions[highlightedIndex].value]
      ) {
        const option = optionRefs.current[filteredOptions[highlightedIndex].value];
        if (option) {
          const container = listboxRef.current;
          const optionTop = option.offsetTop;
          const optionBottom = optionTop + option.offsetHeight;
          const containerTop = container.scrollTop;
          const containerBottom = containerTop + container.offsetHeight;

          if (optionTop < containerTop) {
            container.scrollTop = optionTop;
          } else if (optionBottom > containerBottom) {
            container.scrollTop = optionBottom - container.offsetHeight;
          }
        }
      }
    }, [highlightedIndex, filteredOptions, navigationMode, isWrappingToTop]);

    // Handle option selection
    const handleSelectOption = (
      event: React.MouseEvent | React.KeyboardEvent,
      optionValue: string
    ) => {
      event.stopPropagation();
      event.preventDefault();

      if (onChange) {
        onChange(optionValue);
      }

      // Close the popover after selection
      setTimeout(() => {
        setOpen(false);
      }, 50);
    };

    // Helper function to check if keyboard is currently active
    const isKeyboardNavigationActive = () => {
      return (
        navigationMode === "keyboard" &&
        Date.now() - lastKeyboardNavTime.current < keyboardLockoutPeriod
      );
    };

    // Handle keyboard navigation
    const handleKeyDown = (e: KeyboardEvent<HTMLElement>) => {
      if (filteredOptions.length === 0) return;

      switch (e.key) {
        case "ArrowDown":
        case "ArrowUp":
          e.preventDefault();

          // Set navigation mode to keyboard and record time
          setNavigationMode("keyboard");
          lastKeyboardNavTime.current = Date.now();

          // Handle up/down navigation
          if (e.key === "ArrowDown") {
            setHighlightedIndex((prevIndex) => {
              let newIndex =
                prevIndex >= filteredOptions.length - 1 || prevIndex === -1 ? 0 : prevIndex + 1;

              // Skip disabled options
              while (newIndex < filteredOptions.length && filteredOptions[newIndex].disabled) {
                newIndex = newIndex >= filteredOptions.length - 1 ? 0 : newIndex + 1;
              }

              // If we're looping back to the first item, set the flag to reset scroll
              if (prevIndex === filteredOptions.length - 1 || prevIndex === -1) {
                setIsWrappingToTop(true);
              }

              setActiveDescendant(`option-${filteredOptions[newIndex].value}`);
              return newIndex;
            });
          } else {
            setHighlightedIndex((prevIndex) => {
              let newIndex =
                prevIndex <= 0 || prevIndex === -1 ? filteredOptions.length - 1 : prevIndex - 1;

              // Skip disabled options
              while (newIndex >= 0 && filteredOptions[newIndex].disabled) {
                newIndex = newIndex <= 0 ? filteredOptions.length - 1 : newIndex - 1;
              }

              // No need for special wrapping logic when going up to the bottom
              setActiveDescendant(`option-${filteredOptions[newIndex].value}`);
              return newIndex;
            });
          }
          break;

        case "Enter":
        case "Space":
          e.preventDefault();
          if (
            highlightedIndex >= 0 &&
            highlightedIndex < filteredOptions.length &&
            !filteredOptions[highlightedIndex].disabled
          ) {
            handleSelectOption(e, filteredOptions[highlightedIndex].value);
          }
          break;

        case "Escape":
          // Let Radix UI handle closing
          break;

        default:
          // Don't reset focus for modifier keys
          break;
      }
    };

    // Handle search input changes
    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = e.target.value;
      setSearchTerm(newValue);

      // Clear highlight first, then we'll set it again in the useEffect when filteredOptions changes
      setHighlightedIndex(-1);
      setActiveDescendant("");
    };

    // Handle mouse enter on option items
    const handleMouseEnter = (index: number, optionValue: string) => {
      // Skip highlighting if in keyboard navigation mode and still within lockout period
      if (!isKeyboardNavigationActive()) {
        setHighlightedIndex(index);
        setActiveDescendant(`option-${optionValue}`);
        setNavigationMode("mouse");
      }
    };

    // Handle explicit mouse movement to switch to mouse mode
    const handleMouseMove = () => {
      // Only switch to mouse mode if we're not in the keyboard lockout period
      if (!isKeyboardNavigationActive()) {
        setNavigationMode("mouse");
      }
    };

    return (
      <div className="flex flex-col relative z-0 gap-1" ref={containerRef}>
        {label && <label className="text-muted-foreground text-sm">{label}</label>}

        <Root open={open} onOpenChange={setOpen}>
          <Trigger asChild disabled={disabled}>
            <button
              type="button"
              className={cn(
                variant === "button"
                  ? cn(
                      "flex items-center justify-between gap-2 rounded-md text-xs font-medium transition-all duration-200 focus:outline-none focus-visible:outline-none px-3 py-1.5",
                      disabled
                        ? "text-muted-foreground/50 cursor-not-allowed opacity-50"
                        : value && value !== ""
                          ? activeColorMap[color].chip
                          : "text-muted-foreground hover:bg-muted hover:text-foreground/80",
                      triggerClassName
                    )
                  : cn(
                      "flex items-center justify-between rounded-md border border-border bg-muted px-3 py-2 text-left transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50 focus-visible:ring-offset-2 focus-visible:ring-offset-muted",
                      disabled
                        ? "cursor-not-allowed bg-muted/50 text-muted-foreground"
                        : "cursor-pointer hover:border-primary/50",
                      triggerClassName
                    )
              )}
              disabled={disabled}
              aria-label={label ? `${label} selector` : placeholder}
              onMouseDown={(e) => variant === "button" && e.preventDefault()}
            >
              <span
                className={
                  variant === "button"
                    ? getSelectedLabel() === placeholder
                      ? "text-muted-foreground"
                      : ""
                    : getSelectedLabel() === placeholder
                      ? "text-muted-foreground text-sm"
                      : "text-foreground text-sm"
                }
              >
                {getSelectedLabel()}
              </span>
              {!disabled && (
                <ChevronsUpDown
                  className={cn(
                    variant === "button"
                      ? "text-muted-foreground h-3 w-3 shrink-0 opacity-50"
                      : "text-primary ml-2 h-4 w-4 shrink-0 opacity-50"
                  )}
                />
              )}
            </button>
          </Trigger>

          <Portal>
            <Content
              className={cn(
                "z-[999] w-[var(--radix-popover-trigger-width)] min-w-[220px] rounded-lg bg-card border border-border shadow-lg data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 overflow-hidden",
                contentClassName
              )}
              sideOffset={5}
              align="start"
              alignOffset={0}
              avoidCollisions
              onInteractOutside={() => setOpen(false)}
              onWheel={(e) => e.stopPropagation()}
              style={{ pointerEvents: "auto" }}
            >
              <div className="flex flex-col max-h-[350px]">
                <div className="p-4 flex-shrink-0" onClick={(e) => e.stopPropagation()}>
                  <div className="relative">
                    <InputV3
                      ref={inputRef}
                      type="search"
                      placeholder={searchPlaceholder}
                      value={searchTerm}
                      onChange={handleSearchChange}
                      onClick={(e) => e.stopPropagation()}
                      onKeyDown={handleKeyDown}
                      className={cn(
                        "w-full",
                        variant === "button" && "bg-muted border-border focus:border-border"
                      )}
                      role="combobox"
                      aria-autocomplete="list"
                      aria-expanded={open}
                      aria-controls="searchable-dropdown-listbox"
                      aria-activedescendant={activeDescendant}
                    />
                  </div>
                </div>
                <div className="border-t border-border flex-shrink-0" />
                <div
                  ref={listboxRef}
                  role="listbox"
                  id="searchable-dropdown-listbox"
                  className="overflow-y-auto p-2 max-h-[240px]"
                  onClick={(e) => e.stopPropagation()}
                  onMouseMove={handleMouseMove}
                >
                  {filteredOptions.length > 0 ? (
                    filteredOptions.map((option, index) => (
                      <button
                        key={option.value}
                        id={`option-${option.value}`}
                        ref={(el) => {
                          optionRefs.current[option.value] = el;
                        }}
                        role="option"
                        aria-selected={value === option.value}
                        type="button"
                        className={cn(
                          "flex w-full items-center py-2 px-3 rounded-md text-left transition-all duration-200",
                          "active:scale-95 group",
                          option.disabled
                            ? "cursor-not-allowed opacity-50 text-muted-foreground"
                            : "cursor-pointer",
                          highlightedIndex === index && !option.disabled
                            ? variant === "button"
                              ? "bg-muted text-foreground"
                              : `bg-primary/10 text-foreground ${
                                  navigationMode === "keyboard" ? "ring-1 ring-primary" : ""
                                }`
                            : "",
                          value === option.value
                            ? variant === "button"
                              ? "bg-muted text-foreground font-medium"
                              : "bg-primary/20 text-foreground font-medium"
                            : !option.disabled
                              ? variant === "button"
                                ? "text-foreground/90 hover:text-foreground hover:bg-muted"
                                : "text-foreground/90 hover:text-foreground hover:bg-primary/10"
                              : "text-muted-foreground"
                        )}
                        onClick={(e) => !option.disabled && handleSelectOption(e, option.value)}
                        onMouseEnter={() => handleMouseEnter(index, option.value)}
                        style={{
                          cursor: isKeyboardNavigationActive() ? "default" : "pointer",
                          pointerEvents: isKeyboardNavigationActive() ? "none" : "auto",
                        }}
                        tabIndex={-1}
                      >
                        <Check
                          className={cn(
                            "mr-2 h-4 w-4 transition-opacity duration-200",
                            variant === "button" ? "text-muted-foreground" : "text-primary",
                            value === option.value
                              ? "opacity-100"
                              : highlightedIndex === index
                                ? "opacity-30"
                                : "opacity-0 group-hover:opacity-30"
                          )}
                        />
                        <span className="text-sm group-hover:transform group-hover:translate-x-1 group-active:scale-95 transition-all duration-200">
                          {option.label}
                        </span>
                      </button>
                    ))
                  ) : (
                    <div className="py-2 text-center text-muted-foreground text-xs">
                      No matches found
                    </div>
                  )}
                  {showActionButton && (
                    <div className="p-2 pb-0">
                      <Button
                        variant="ghostSecondary"
                        size="sm"
                        onClick={onActionButtonClick}
                        className={actionButtonClassName}
                      >
                        {actionButtonContent}
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            </Content>
          </Portal>
        </Root>

        {error && <p className="text-danger text-xs mt-1">{error.message}</p>}
      </div>
    );
  }
);

// Add display name for better debugging
SearchableDropdown.displayName = "SearchableDropdown";
